import { List } from '@chakra-ui/react';
import { IAccountRespSchema } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { formatInteger, pluralize } from '@monetize/utils/core';
import { useState } from 'react';
import {
  MAlert,
  MBox,
  MButton,
  MCenterModal,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
} from '../../../components/Monetize';
import { useGetAccountRecords } from './AccountMerge.hooks';

interface AccountMergeConfirmationModalProps {
  sourceAccount: IAccountRespSchema;
  targetAccount: IAccountRespSchema;
  sourceAccountRecords: Maybe<ReturnType<typeof useGetAccountRecords>>;
  targetAccountRecords: Maybe<ReturnType<typeof useGetAccountRecords>>;
  onCancel: () => void;
  onConfirm: (params: {
    sourceAccountId: string;
    targetAccountId: string;
    retainSourcePaymentGatewayAccount: boolean;
  }) => Promise<void>;
}

export const AccountMergeConfirmationModal = ({
  sourceAccount,
  targetAccount,
  sourceAccountRecords,
  targetAccountRecords,
  onCancel,
  onConfirm,
}: AccountMergeConfirmationModalProps) => {
  const sourceGatewayAccount = sourceAccountRecords?.gatewayCustomerAccount;
  const targetGatewayAccount = targetAccountRecords?.gatewayCustomerAccount;
  const [isLoading, setIsLoading] = useState(false);
  const [gatewayAccountId, setGatewayAccountId] = useState<string | null>(
    () => {
      if (sourceGatewayAccount && !targetGatewayAccount) {
        return sourceGatewayAccount.gatewayAccount.id;
      }
      if (!sourceGatewayAccount && targetGatewayAccount) {
        return targetGatewayAccount.gatewayAccount.id;
      }
      return null;
    },
  );

  const showPaymentGatewayRetainOption =
    !!sourceGatewayAccount && !!targetGatewayAccount;

  const saveButtonDisabled =
    showPaymentGatewayRetainOption && !gatewayAccountId;

  function handleConfirm() {
    setIsLoading(true);
    onConfirm({
      sourceAccountId: sourceAccount.id,
      targetAccountId: targetAccount.id,
      retainSourcePaymentGatewayAccount:
        !!gatewayAccountId &&
        gatewayAccountId === sourceGatewayAccount?.gatewayAccount.id,
    }).finally(() => setIsLoading(false));
  }

  return (
    <MCenterModal
      isOpen
      onClose={() => onCancel()}
      closeOnOverlayClick={false}
      modalContentProps={
        { 'data-testid': 'account-merge-confirmation-modal' } as any
      }
      modalTitle="Are you sure you want to merge these accounts?"
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton onClick={() => onCancel()} variant="cancel" minW="auto">
            Cancel
          </MButton>
          <MButton
            variant="primary"
            disabled={saveButtonDisabled || isLoading}
            isLoading={isLoading}
            onClick={handleConfirm}
            minW="auto"
          >
            Merge Accounts
          </MButton>
        </MStack>
      )}
    >
      <MBox>
        <MAlert type="warning" mb={4} fontWeight={600}>
          This action is irreversible.
        </MAlert>
        {showPaymentGatewayRetainOption && (
          <MAlert type="info" mb={4}>
            <MText>
              Both accounts are linked to an account on your payment gateway.
              Which one do you want to use on the remaining account after the
              merge?
            </MText>
            <MRadioGroup
              mt={2}
              name="retainGatewayAccount"
              value={gatewayAccountId || ''}
              onChange={(value) => setGatewayAccountId(value ?? '')}
            >
              <MRadio value={sourceGatewayAccount?.gatewayAccount.id ?? ''}>
                {sourceGatewayAccount?.gatewayAccount.name} (
                {sourceGatewayAccount?.gatewayAccount.id})
              </MRadio>
              {sourceAccountRecords && (
                <List.Root variant="plain" ml={8} mb={2}>
                  <List.Item>
                    {formatInteger(sourceAccountRecords.paymentMethodCount)}{' '}
                    {pluralize(
                      'payment method',
                      sourceAccountRecords.paymentMethodCount,
                    )}
                  </List.Item>
                  <List.Item>
                    {formatInteger(sourceAccountRecords.paymentCount)}{' '}
                    {pluralize('payment', sourceAccountRecords.paymentCount)}
                  </List.Item>
                </List.Root>
              )}
              <MRadio value={targetGatewayAccount?.gatewayAccount.id ?? ''}>
                {targetGatewayAccount?.gatewayAccount.name} (
                {targetGatewayAccount?.gatewayAccount.id})
              </MRadio>
              {targetAccountRecords && (
                <List.Root variant="plain" ml={8} mb={2}>
                  <List.Item>
                    {formatInteger(targetAccountRecords.paymentMethodCount)}{' '}
                    {pluralize(
                      'payment method',
                      targetAccountRecords.paymentMethodCount,
                    )}
                  </List.Item>
                  <List.Item>
                    {formatInteger(targetAccountRecords.paymentCount)}{' '}
                    {pluralize('payment', targetAccountRecords.paymentCount)}
                  </List.Item>
                </List.Root>
              )}
            </MRadioGroup>
          </MAlert>
        )}
        <MBox textAlign="center">
          <MText>All related records will be moved from</MText>
          <MText as="span" fontWeight={600}>
            {sourceAccount.accountName} ({sourceAccount.id})
          </MText>{' '}
          <MText>to</MText>
          <MText as="span" fontWeight={600}>
            {targetAccount.accountName} ({targetAccount.id})
          </MText>
        </MBox>
      </MBox>
    </MCenterModal>
  );
};
